import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { format } from "date-fns";
import { Calendar, ChevronLeft, Clock, Loader2 } from "lucide-react";

import { useAuth } from "@/hooks/useAuth";
import { useGlobalAdmin } from "@/hooks/useGlobalAdmin";
import { supabase } from "@/integrations/supabase/client";
import { sanitizeHtml } from "@/lib/sanitize";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface BlogPostData {
  id: string;
  site_id: string;
  slug: string;
  title: string;
  category: string;
  topic: string;
  content_html: string;
  excerpt: string | null;
  main_image_url: string | null;
  main_image_alt: string | null;
  published_at: string;
  is_published: boolean;
}

export default function BlogPreview() {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { loading: globalAdminLoading } = useGlobalAdmin();

  const [post, setPost] = useState<BlogPostData | null>(null);
  const [loading, setLoading] = useState(true);

  const readingTime = useMemo(() => {
    if (!post?.content_html) return "";
    const text = post.content_html.replace(/<[^>]*>/g, " ");
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    return `${Math.max(1, Math.ceil(words / 200))} min read`;
  }, [post?.content_html]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/admin/login");
      return;
    }

    if (!authLoading && !globalAdminLoading && user && postId) {
      (async () => {
        try {
          setLoading(true);
          const { data, error } = await supabase
            .from("blog_posts")
            .select(
              "id, site_id, slug, title, category, topic, content_html, excerpt, main_image_url, main_image_alt, published_at, is_published"
            )
            .eq("id", postId)
            .maybeSingle();

          if (error) throw error;
          setPost((data as BlogPostData) ?? null);
        } catch (e) {
          console.error("Failed to load blog preview:", e);
          setPost(null);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [authLoading, globalAdminLoading, user, postId, navigate]);

  if (authLoading || globalAdminLoading || loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">Post not found</p>
          <Button variant="outline" onClick={() => navigate("/admin/blog")}>Back to Blog</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{`Preview: ${post.title}`}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <header className="border-b border-border bg-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Badge variant="secondary">Admin Preview</Badge>
            {!post.is_published && <Badge variant="outline">Draft</Badge>}
            {new Date(post.published_at) > new Date() && <Badge variant="outline">Scheduled</Badge>}
            <span className="truncate text-sm text-muted-foreground">{post.slug}</span>
          </div>
          <Button variant="ghost" onClick={() => navigate("/admin/blog")}>
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mb-4">
              <Badge variant="secondary">{post.category}</Badge>
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {format(new Date(post.published_at), "MMM d, yyyy 'at' h:mm a")}
              </span>
              {readingTime && (
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {readingTime}
                </span>
              )}
            </div>

            <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-2">
              {post.title}
            </h1>
            <p className="text-sm text-muted-foreground mb-6">Topic: {post.topic}</p>

            {post.main_image_url && (
              <figure className="mb-8 rounded-lg overflow-hidden">
                <img
                  src={post.main_image_url}
                  alt={post.main_image_alt || post.title}
                  loading="lazy"
                  className="w-full h-auto object-cover aspect-video"
                />
              </figure>
            )}

            <div
              className="prose prose-lg max-w-none 
                prose-headings:font-display prose-headings:text-foreground
                prose-p:text-muted-foreground prose-p:leading-relaxed
                prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                prose-strong:text-foreground
                prose-ul:text-muted-foreground prose-ol:text-muted-foreground
                prose-li:marker:text-primary"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.content_html) }}
            />
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
